import * as React from 'react';
import { memo, useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import { SKILL_LOGOS } from './skillLogos';

/**
 * Иконка навыка с тремя уровнями отката (ТЗ 0039, доводка UX).
 *
 * 1. картинка, загруженная оператором в ERP;
 * 2. встроенный знак технологии по `slug`;
 * 3. буквенная плашка цветом категории.
 *
 * 🔴 Третий уровень — не заглушка «иконка не найдена», а полноценный вариант.
 * У «командной работы», «статистики» и «сетей» знака не существует в природе,
 * и ждать его бессмысленно; у Azure и C# он есть, но перераспространять его
 * нельзя. Если бы откат рисовал серый вопросительный знак, карта навыков
 * выглядела бы наполовину сломанной ВСЕГДА.
 *
 * Иконка декоративна: она стоит рядом с названием навыка, поэтому помечена
 * `aria-hidden`. Читалка озвучит название один раз, а не дважды.
 */
export interface ISkillIcon {
  /** `Skill.slug` — по нему ищется встроенный знак. */
  slug: string;
  /** Название навыка: из него берётся буква для плашки. */
  name: string;
  /** `Skill.category` — красит плашку. Неизвестная категория даёт серую. */
  category?: string | null;
  /** Картинка из ERP. Старше встроенного знака: оператор знает лучше. */
  iconUrl?: string | null;
  /** Сторона квадрата в пикселях. */
  size?: number;
}

/**
 * Цвет буквенной плашки по категории. Не из палитры приложения намеренно:
 * это ось СМЫСЛА (язык / фреймворк / облако), а не акцент интерфейса, и она
 * обязана читаться одинаково в обоих фронтах.
 */
const CATEGORY_COLORS: Record<string, string> = {
  language: '#3E63DD',
  framework: '#7C3AED',
  library: '#0891B2',
  tool: '#475569',
  database: '#047857',
  cloud: '#B45309',
  domain: '#BE185D',
  soft: '#C2410C',
};

const FALLBACK_COLOR = '#64748B';

const Box = styled.span<{ $size: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: ${({ $size }) => Math.round($size * 0.22)}px;
  overflow: hidden;
`;

const Photo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const Letter = styled(Box)<{ $bg: string; $size: number }>`
  background: ${({ $bg }) => $bg};
  color: #ffffff;
  font-size: ${({ $size }) => Math.round($size * 0.5)}px;
  line-height: 1;
  font-weight: 600;
  user-select: none;
`;

/**
 * Первая БУКВА, а не первый символ: у «.NET» и «C#» первый символ — знак
 * препинания, и плашка получалась бы пустой точкой.
 */
const initialOf = (name: string): string => {
  const letter = (name || '').replace(/[^\p{L}\p{N}]/gu, '').charAt(0);
  return (letter || '?').toUpperCase();
};

export const SkillIcon = memo(({ slug, name, category, iconUrl, size = 20 }: ISkillIcon) => {
  const logo = useMemo(() => SKILL_LOGOS[slug] ?? null, [slug]);
  // 🔴 Битая ссылка обязана ОТКАТИТЬСЯ, а не остаться сломанной
  // картинкой. Ровно ради этого написан третий уровень: объект могли
  // удалить с бакета, и без отката карта навыков выглядела бы
  // наполовину сломанной от одной мёртвой ссылки. Сбрасываем при смене
  // адреса, иначе новая картинка унаследовала бы отказ старой.
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [iconUrl]);

  if (iconUrl && !broken) {
    return (
      <Box $size={size}>
        <Photo src={iconUrl} alt="" aria-hidden onError={() => setBroken(true)} />
      </Box>
    );
  }

  if (logo) {
    return (
      <Box $size={size}>
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
          focusable="false">
          <path d={logo.d} fill={logo.color} />
        </svg>
      </Box>
    );
  }

  return (
    <Letter $size={size} $bg={CATEGORY_COLORS[category || ''] || FALLBACK_COLOR} aria-hidden>
      {initialOf(name)}
    </Letter>
  );
});

SkillIcon.displayName = 'SkillIcon';
